
import toast from "react-hot-toast";
import * as services from "../services/inventoryService";
import api from "../../../services/api";

export const useInventoryActions = (state, dataHandlers, formHandlers) => {
  const { 
    setInventoryState, setPurchases, setAddOpen, setPurchaseOpen, setOpenStockOpen, 
    setRestockItem, setRestockQty, setDispatchReq, setRestockReturn, restockReturn, activeTab, 
    editingItem, setSubmitting, setSelReq, setSelReturn
  } = state;
  const { refreshAll } = dataHandlers;
  const { form, resetForm } = formHandlers;

    const addItem = async (data) => {
    console.log("addItem called with data:", data);
    const { openStockOpen } = state;
    console.log("Current state.openStockOpen:", openStockOpen);

    try {
      state.setLoading(true);
      const payload = (data && typeof data.preventDefault === 'function') ? form : (data || form);
      
      const isMultiItem = payload.items && payload.items.length > 0;

      if (openStockOpen) {
        console.log("Entering Open Stock Flow with payload:", payload);
        // Open Stock flow — uses OpenStockEntryModal (unitPrice/availableQty fields)
        const rate = Number(payload.unitPrice) || 0;
        const qty = Number(payload.availableQty) || 0;
        const baseAmount = rate * qty;
        const taxPct = Number(payload.taxP) || 0;
        const taxableValue = baseAmount;
        const totalTax = (taxableValue * taxPct) / 100;
        const netBill = taxableValue + totalTax;

        // store dropdown value is always the _id (ObjectId string)
        const resolvedStore = payload.store;
        if (!resolvedStore) {
          toast.error("Store is required. Please select a store.");
          return;
        }

        // Resolve master item — search by _id, itemName, or name
        const itemName = (payload.name || '').trim().toLowerCase();
        const masterItem = (state.masterItems || []).find(mi =>
          mi._id === payload._id ||
          mi._id === payload.item || // Check item field too
          mi._id?.toString() === payload._id ||
          (mi.itemName || '').trim().toLowerCase() === itemName ||
          (mi.name || '').trim().toLowerCase() === itemName
        );

        const resolvedItemId = masterItem?._id;
        if (!resolvedItemId) {
          console.error("Item resolution failed for:", itemName);
          throw new Error(`Item "${payload.name}" not found in master list. Please search and select an existing item first.`);
        }

        // Resolve unit ID
        const unitObj = (state.units || []).find(u => 
          u._id === payload.unit || 
          (u.title || u.name) === payload.unit
        );

        // Resolve category ID
        const catObj = (state.cats || []).find(c => 
          c._id === payload.category || 
          (c.title || c.name) === payload.category
        );

        // Resolve brand ID
        const brandObj = (state.brands || []).find(b => 
          b._id === payload.brand || 
          (b.title || b.name) === payload.brand
        );

        const openStockPayload = {
          name: payload.name,
          itemCode: payload.itemCode,
          store: resolvedStore,
          invoiceDate: payload.invoiceDate || new Date().toISOString().split('T')[0],
          type: payload.type || 'Stockable',
          category: catObj?._id || payload.category,
          brand: brandObj?._id || payload.brand,
          hsnCode: payload.hsnCode,
          isUnique: payload.isUnique || false,
          uniqueName: payload.uniqueName,
          hasBatchExpiry: payload.hasBatchExpiry || false,
          expiryDate: payload.expiryDate,
          barcode: payload.barcode,
          unit: unitObj?._id || payload.unit,
          description: payload.description,
          rate,
          quantity: qty,
          availableQty: qty,
          initialQty: qty,
          bvPoints: Number(payload.bvPoints) || 0,
          baseAmount,
          netPayment: netBill,
          serviceType: activeTab // to keep context if needed
        };

        if (editingItem) {
          await services.updateOpeningStock(editingItem._id, openStockPayload);
          toast.success("Closing stock updated!");
        } else {
          await services.createOpeningStock(openStockPayload);
          toast.success("Closing stock recorded in dedicated table!");
        }

      } else if (state.purchaseOpen) {
        // Regular purchase flow - Validate mandatory fields
        if (!payload.supplier) { toast.error("Vendor/Supplier is mandatory for purchases"); return; }
        if (!payload.invoiceNo) { toast.error("Invoice Number is mandatory for purchases"); return; }
        // updated by nithin
        // reason: Store is no longer mandatory for purchases
        // if (!payload.store) { toast.error("Store selection is mandatory for purchases"); return; }

        const itemsToProcess = isMultiItem ? payload.items : [payload];
        
        const itemsTotal = itemsToProcess.reduce((sum, it) => sum + (Number(it.netPayment) || 0), 0);
        const taxableTotal = itemsToProcess.reduce((sum, it) => sum + (Number(it.taxable) || 0), 0);
        const taxTotal = itemsToProcess.reduce((sum, it) => sum + (Number(it.taxAmount) || 0), 0);
        const discountTotal = itemsToProcess.reduce((sum, it) => sum + (Number(it.discount) || 0), 0);

        const vendorObj = (state.suppliers || []).find(s => (s.name || s.title || s) === payload.supplier || s._id === payload.supplier);
        const storeObj = (state.stores || []).find(st => (st.title || st.name || st) === payload.store || st._id === payload.store);

        const purchasePayload = {
          vendor: vendorObj?._id || payload.supplier, 
          store: storeObj?._id || payload.store,
          invoiceNo: payload.invoiceNo,
          invoiceNumber: payload.invoiceNo,
          billNo: payload.invoiceNo,
          date: payload.invoiceDate,
          invoiceDate: payload.invoiceDate,
          serviceType: activeTab,
          buyer: payload.businessUnit || "VENUS INFRA SERVICES",
          items: itemsToProcess.map(it => ({
            item: it.item || it._id || (state.masterItems || []).find(mi => mi.itemName === it.name)?._id,
            qty: Number(it.quantity) || 0,
            rate: Number(it.rate) || 0,
            taxPercent: Number(it.taxP) || 0,
            taxAmount: Number(it.taxAmount) || 0,
            amount: Number(it.taxable) || 0,
            discountPercent: Number(it.discP) || 0,
            discountAmount: Number(it.discount) || 0,
            netBill: Number(it.netBill) || 0,
            finalNetPayment: Number(it.netBill) || 0,
            // Include unique and batch tracking details
            isUnique: it.isUnique || false,
            uniqueName: it.uniqueName || "",
            hasBatch: it.hasBatchExpiry || it.hasBatch || false
          })),
          totalDiscount: discountTotal,
          taxableAmount: taxableTotal,
          totalTax: taxTotal,
          netBill: itemsTotal,
          adjustment: Number(payload.adjustments) || 0,
          netPayable: itemsTotal + (Number(payload.adjustments) || 0),
          paidAmount: Number(payload.netPayment) || 0,
          paymentMode: 'bank'
        };

        await services.createPurchase(purchasePayload);
        toast.success("Purchase recorded successfully!");
      } else if (editingItem) {
        await services.updateInventoryItem(editingItem._id, payload);
        toast.success("Item updated!");
      } else {
        await services.addItem({ ...payload, serviceType: activeTab });
        toast.success("Item added!");
      }

      setAddOpen(false); setPurchaseOpen(false); setOpenStockOpen(false);
      refreshAll();
      resetForm();
    } catch (err) { 
      console.error("Submission error details:", err);
      const msg = err.response?.data?.message || err.message || "Operation failed";
      toast.error(msg); 
    }
    finally { 
      console.log("addItem submission flow completed.");
      state.setLoading(false); 
    }
  };

  const addSale = async (payload) => {
    try {
      state.setLoading(true);
      await services.createSale(payload);
      toast.success("Sale recorded successfully!");
      state.setSaleOpen(false);
      refreshAll();
      return true;
    } catch (err) {
      console.error("Sale submission error:", err);
      toast.error(err.response?.data?.message || err.message || "Sale creation failed");
      return false;
    } finally {
      state.setLoading(false);
    }
  };

  const deleteItem = async (itemOrId) => {
    if (!window.confirm("Are you sure?")) return;
    const id = typeof itemOrId === 'string' ? itemOrId : itemOrId._id;
    const isOpeningStock = typeof itemOrId === 'object' && (itemOrId.invoiceNo === 'OPEN-STOCK' || itemOrId.supplier === 'Opening Stock' || !itemOrId.invoiceNo);
    
    try {
      if (isOpeningStock && !itemOrId.isMaster) {
         await services.deleteOpeningStock(id);
      } else {
         await services.deleteItem(id);
      }
      refreshAll();
      toast.success("Item deleted!");
    } catch (err) { toast.error("Delete failed"); }
  };

  const restock = async (restockItem, restockQty) => {
    if (!restockItem || !restockQty) return;
    try {
      setSubmitting(true);
      const amount = Number(restockQty);
      
      // Resolve the ID: If it's a purchase record, we need the item's master ID to update inventory state
      const targetId = restockItem.item?._id || (typeof restockItem.item === 'string' ? restockItem.item : restockItem._id);
      const isPurchase = !!(restockItem.invoiceNumber || restockItem.billNo || restockItem.invoiceNo);

      if (isPurchase) {
        // 1. Purchase records have their own sync logic in the backend (it increments both purchase and inventory)
        await services.updatePurchase(restockItem._id, {
          stockAction: 'increment',
          stockAmount: amount
        }, restockItem.isFallback);
      } else {
        // 2. Direct inventory record update (for Opening Stock or manual entries)
        await services.updateInventoryItem(targetId, { 
          stockAction: 'increment', 
          stockAmount: amount 
        });
      }

      // 3. Update local state
      // Update inventory master
      setInventoryState(p => p.map(i => {
        const idToMatch = i._id;
        if (idToMatch === targetId || idToMatch === restockItem._id) {
          const currentStock = Number(i.availableQty || i.stock || 0);
          return { ...i, availableQty: currentStock + amount, stock: currentStock + amount };
        }
        return i;
      }));

      // Update purchases list if needed
      if (isPurchase) {
        setPurchases(p => p.map(i => {
          if (i._id === restockItem._id) {
            return { ...i, quantity: (i.quantity || 0) + amount };
          }
          return i;
        }));
      }

      // Refresh data to ensure all stats/counts are correct
      if (typeof refreshAll === 'function') refreshAll();

      toast.success(`${restockItem.itemName || restockItem.name || 'Item'} restocked!`);
      setRestockItem(null); setRestockQty("");
    } catch (err) { 
      console.error("Restock error:", err);
      toast.error("Restock failed"); 
    } finally {
      setSubmitting(false);
    }
  };

  const handleDispatch = async (request, dispatchData) => {
    try {
      const requestId = typeof request === 'string' ? request : (request?._id || request?.id);
      const isTower = activeTab === 'tower' || (typeof request === 'object' && (request?.serviceType === 'tower' || request?.type === 'tower'));
      
      if (isTower) {
        // Tower module uses a different endpoint and PUT method
        await api.put(`/material-requests/${requestId}/dispatch`, dispatchData);
      } else {
        // Standard inventory/solar endpoint
        await api.put(`/inventory/material-requests/${requestId}/status`, dispatchData);
      }
      
      toast.success("Materials dispatched!");
      refreshAll();
      setDispatchReq(null);
    } catch (error) { 
      console.error("Dispatch error:", error);
      toast.error(error.response?.data?.message || "Dispatch failed"); 
    }
  };

  const handleDeliver = async (request) => {
    try {
      const requestId = typeof request === 'string' ? request : (request?._id || request?.id);
      const isTower = activeTab === 'tower' || (typeof request === 'object' && (request?.serviceType === 'tower' || request?.type === 'tower'));

      if (isTower) {
        await api.put(`/material-requests/${requestId}/status`, { status: "DELIVERED" });
      } else {
        await api.put(`/inventory/material-requests/${requestId}/status`, { status: "DELIVERED" });
      }

      toast.success("Materials delivered!");
      setSelReq(null);
      refreshAll();
    } catch (error) { 
      console.error("Deliver error:", error);
      toast.error("Update failed"); 
    }
  };

  const handleConfirmReturn = async (itemOrId, action = 'receive') => {
    try {
      const id = typeof itemOrId === 'string' ? itemOrId : itemOrId._id;
      
      if (action === 'restock') {
        setRestockReturn(itemOrId);
        return;
      }

      await api.patch(`/inventory/material-returns/${id}/status`, { status: "RECEIVED" });
      toast.success("Return processed!");
      setSelReturn(null);
      refreshAll();
    } catch (error) { toast.error("Failed to process return"); }
  };

  const finalRestockReturn = async (id, restockData) => {
    try {
      setSubmitting(true);
      const isPurchase = restockReturn?.rawType === 'purchase';
      if (isPurchase) {
        await api.post(`/inventory/purchase-returns/${id}/restock`, { items: restockData });
      } else {
        await api.post(`/inventory/material-returns/${id}/restock`, { items: restockData });
      }
      toast.success("Inventory updated from return!");
      setRestockReturn(null);
      refreshAll();
    } catch (err) {
      console.error("Restock error:", err);
      toast.error(err.response?.data?.message || "Restock failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePurchaseReturnPayment = async (pr, paymentData) => {
    try {
      await api.post(`/inventory/purchase-returns/${pr._id}/payments`, paymentData);
      toast.success("Payment recorded!");
      refreshAll();
    } catch (err) {
      toast.error("Failed to record payment");
    }
  };

  const handleSaleReturnPayment = async (sr, paymentData) => {
    try {
      await api.patch(`/inventory/material-returns/${sr._id}/payment`, paymentData);
      toast.success("Payment recorded!");
      refreshAll();
    } catch (err) {
      toast.error("Failed to record payment");
    }
  };

  const ownerAction = async (id, action, extra = {}) => {
    try {
      if (action === "owner_approved") {
        await services.ownerApproveRequest(id);
        toast.success("Request approved by owner!");
      } else if (action === "delivered") {
        await handleDeliver(id, extra);
      } else {
        await services.updateRequestStatus(id, action);
        toast.success(`Request marked as ${action}`);
      }
      refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.message || "Operation failed");
    }
  };

  return {
    addItem,
    addSale,
    deleteItem,
    restock,
    handleDispatch,
    handleDeliver,
    handleConfirmReturn,
    finalRestockReturn,
    handlePurchaseReturnPayment,
    handleSaleReturnPayment,
    ownerAction
  };
};
